// ============================================================
// Logger Interface
// ============================================================

export type LogLevel = "debug" | "info" | "warn" | "error";

export interface LogContext {
  [key: string]: unknown;
}

export interface Logger {
  debug(msg: string, ctx?: LogContext): void;
  info(msg: string, ctx?: LogContext): void;
  warn(msg: string, ctx?: LogContext): void;
  error(msg: string, error?: Error, ctx?: LogContext): void;
  child(ctx: LogContext): Logger;
}

// ============================================================
// Strukturierter Console-Logger
// ============================================================
// Gibt JSON-formatierte Log-Einträge aus — maschinenlesbar und
// einfach in externe Logging-Dienste (Sentry, Datadog) integrierbar.

const LEVEL_VALUES: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

interface ConsoleLoggerOptions {
  minLevel?: LogLevel;
  context?: LogContext;
}

export class ConsoleLogger implements Logger {
  private readonly minLevel: number;
  private readonly baseContext: LogContext;

  constructor(options: ConsoleLoggerOptions = {}) {
    this.minLevel = LEVEL_VALUES[options.minLevel ?? "debug"];
    this.baseContext = options.context ?? {};
  }

  private log(level: LogLevel, msg: string, extra?: LogContext, error?: Error): void {
    if (LEVEL_VALUES[level] < this.minLevel) return;

    const entry: Record<string, unknown> = {
      level,
      msg,
      ts: new Date().toISOString(),
      ...this.baseContext,
      ...extra,
    };

    if (error !== undefined) {
      entry["error"] = error.message;
      entry["stack"] = error.stack;
    }

    const output = JSON.stringify(entry);

    switch (level) {
      case "debug":
        console.debug(output);
        break;
      case "info":
        console.info(output);
        break;
      case "warn":
        console.warn(output);
        break;
      case "error":
        console.error(output);
        break;
    }
  }

  debug(msg: string, ctx?: LogContext): void {
    this.log("debug", msg, ctx);
  }

  info(msg: string, ctx?: LogContext): void {
    this.log("info", msg, ctx);
  }

  warn(msg: string, ctx?: LogContext): void {
    this.log("warn", msg, ctx);
  }

  error(msg: string, error?: Error, ctx?: LogContext): void {
    this.log("error", msg, ctx, error);
  }

  /** Erstellt einen Kind-Logger der den Basis-Kontext um weitere Felder erweitert. */
  child(ctx: LogContext): Logger {
    return new ConsoleLogger({
      minLevel: Object.keys(LEVEL_VALUES).find(
        (k) => LEVEL_VALUES[k as LogLevel] === this.minLevel
      ) as LogLevel,
      context: { ...this.baseContext, ...ctx },
    });
  }
}

// ============================================================
// No-op Logger (für Tests)
// ============================================================

export class NoopLogger implements Logger {
  debug(): void {}
  info(): void {}
  warn(): void {}
  error(): void {}
  child(): Logger { return this; }
}

// ============================================================
// Default-Export: Logger-Instanz für die App
// ============================================================

export const logger: Logger = new ConsoleLogger({
  minLevel: (import.meta as { env?: { DEV?: boolean } }).env?.DEV ? "debug" : "info",
});
